import React, { useState } from 'react';
import { useModelStore } from '../../../store/useModelStore';
import { useUIStore } from '../../../store/useUIStore';

interface DynamicAnalysisProps {
  onAnalysisRun?: (type: string) => void;
}

export const DynamicAnalysis: React.FC<DynamicAnalysisProps> = ({ onAnalysisRun }) => {
  const [analysisOptions, setAnalysisOptions] = useState({
    analysisType: 'modal' as 'modal' | 'timeHistory' | 'responseSpectrum',
    numberOfModes: 12,
    frequencyRange: { min: 0, max: 100 },
    dampingRatio: 0.05,
    timeStep: 0.01,
    totalTime: 10
  });

  const { showNotification } = useUIStore();
  const { elements, nodes } = useModelStore();

  const handleRunAnalysis = () => {
    if (elements.length === 0 || nodes.length === 0) {
      showNotification('warning', 'No Model Data', 'Please create a model before running analysis');
      return;
    }

    showNotification('info', 'Analysis Started', `${analysisOptions.analysisType} analysis is running...`);
    onAnalysisRun?.('dynamic');
    
    // Simulate analysis
    setTimeout(() => {
      showNotification('success', 'Analysis Complete', `${analysisOptions.analysisType} analysis completed successfully`);
    }, 3000);
  };

  return (
    <div className="dynamic-analysis-panel">
      <div className="panel-header">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Dynamic Analysis</h3>
      </div>
      
      <div className="panel-content space-y-6">
        <div className="analysis-type">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Analysis Type</h4>
          
          <div className="space-y-2">
            {['modal', 'timeHistory', 'responseSpectrum'].map((type) => (
              <label key={type} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="analysisType"
                  value={type}
                  checked={analysisOptions.analysisType === type}
                  onChange={(e) => setAnalysisOptions({
                    ...analysisOptions,
                    analysisType: e.target.value as any
                  })}
                  className="rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                  {type === 'timeHistory' ? 'Time History' : 
                   type === 'responseSpectrum' ? 'Response Spectrum' : type}
                </span>
              </label>
            ))}
          </div>
        </div>

        {analysisOptions.analysisType === 'modal' && (
          <div className="modal-options">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Modal Analysis Options</h4>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Number of Modes
                </label>
                <input
                  type="number"
                  value={analysisOptions.numberOfModes}
                  onChange={(e) => setAnalysisOptions({
                    ...analysisOptions,
                    numberOfModes: parseInt(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  max="100"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Min Frequency (Hz)
                  </label>
                  <input
                    type="number"
                    value={analysisOptions.frequencyRange.min}
                    onChange={(e) => setAnalysisOptions({
                      ...analysisOptions,
                      frequencyRange: {
                        ...analysisOptions.frequencyRange,
                        min: parseFloat(e.target.value)
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Frequency (Hz)
                  </label>
                  <input
                    type="number"
                    value={analysisOptions.frequencyRange.max}
                    onChange={(e) => setAnalysisOptions({
                      ...analysisOptions,
                      frequencyRange: {
                        ...analysisOptions.frequencyRange,
                        max: parseFloat(e.target.value)
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {analysisOptions.analysisType === 'timeHistory' && (
          <div className="time-history-options">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Time History Options</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time Step (s)
                </label>
                <input
                  type="number"
                  value={analysisOptions.timeStep}
                  onChange={(e) => setAnalysisOptions({
                    ...analysisOptions,
                    timeStep: parseFloat(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0.001"
                  step="0.001"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Total Time (s)
                </label>
                <input
                  type="number"
                  value={analysisOptions.totalTime}
                  onChange={(e) => setAnalysisOptions({
                    ...analysisOptions,
                    totalTime: parseFloat(e.target.value)
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0.1"
                />
              </div>
            </div>
          </div>
        )}

        <div className="damping-settings">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Damping Settings</h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Damping Ratio
            </label>
            <input
              type="number"
              value={analysisOptions.dampingRatio}
              onChange={(e) => setAnalysisOptions({
                ...analysisOptions,
                dampingRatio: parseFloat(e.target.value)
              })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
              max="1"
              step="0.01"
            />
          </div>
        </div>

        <div className="analysis-actions">
          <button
            onClick={handleRunAnalysis}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Run Dynamic Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default DynamicAnalysis;
